import { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { ModalConfirmAction } from "components/modals/ModalConfirmAction"
import { TablePagination } from "components/paginations/TablePagination"

import { cmsSeoService } from "services/cms-seo/cms-seo.service"
import { CmsMediaItem } from "services/cms-seo/type"

import { AsyncList, FilterField, InfoBox, inputClass, PathText, primaryButtonClass } from "../CmsUi"
import { formatBytes } from "../helpers/format"

const PAGE_SIZE = 24

export const MediaTab: FC = () => {
  const { t } = useTranslation("translation", { useSuspense: false })

  const [items, setItems] = useState<CmsMediaItem[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [deleteCandidate, setDeleteCandidate] = useState<CmsMediaItem | null>(null)
  const [altDrafts, setAltDrafts] = useState<Record<string, string>>({})
  const fileInput = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const data = await cmsSeoService.listMedia({ page, pageSize: PAGE_SIZE, q: search.trim() || undefined })
    setItems(data.items)
    setTotalPages(Math.max(data.total_pages, 1))
    setLoading(false)
  }, [page, search])

  useEffect(() => {
    load()
  }, [load])

  const onUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ""
    if (!file) return

    const uploaded = await cmsSeoService.uploadMedia(file, "")
    if (uploaded) {
      toast.success(t("cmsSeo.mediaUploaded", { defaultValue: "File uploaded — add alt text before using it" }) as string)
      load()
    }
  }

  const saveAlt = async (item: CmsMediaItem) => {
    const alt = altDrafts[item.id] ?? item.alt
    if (alt === item.alt) return
    if (await cmsSeoService.updateMedia(item.id, { alt })) {
      toast.success(t("cmsSeo.altSaved", { defaultValue: "Alt text saved" }) as string)
      load()
    }
  }

  const confirmDelete = async (item: CmsMediaItem) => {
    if (item.used_by.length > 0) {
      toast.error(t("cmsSeo.mediaInUse", { defaultValue: "This file is still used by a page" }) as string)
      return
    }
    if (await cmsSeoService.deleteMedia(item.id)) {
      toast.success(t("cmsSeo.mediaDeleted", { defaultValue: "File deleted" }) as string)
      load()
    }
  }

  return (
    <div className="flex flex-col gap-5 p-4 sm:p-5">
      <div className="flex flex-wrap items-end gap-4">
        <FilterField label={t("common.search", { defaultValue: "Search" })} grow>
          <input
            className={inputClass}
            value={search}
            onChange={(event) => {
              setPage(1)
              setSearch(event.target.value)
            }}
            placeholder={t("cmsSeo.filter.searchMedia", { defaultValue: "Search media…" }) as string}
          />
        </FilterField>

        <input ref={fileInput} type="file" accept="image/*" className="hidden" onChange={onUpload} />
        <button
          type="button"
          className={`${primaryButtonClass} w-full sm:w-auto`}
          onClick={() => fileInput.current?.click()}>
          {t("cmsSeo.upload", { defaultValue: "+ Upload" })}
        </button>
      </div>

      <InfoBox>
        {t("cmsSeo.mediaInfo", {
          defaultValue:
            "Media library — images used in article bodies, featured images and Open Graph cards. Every file stores alt text, dimensions and the pages using it. A file still referenced by a page cannot be deleted."
        })}
      </InfoBox>

      <AsyncList
        loading={loading}
        isEmpty={items.length === 0}
        emptyText={t("cmsSeo.noMedia", { defaultValue: "The library is empty." }) as string}
      >
        <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
          {items.map((item) => (
            <div key={item.id} className="overflow-hidden rounded-xl border border-black/[0.07] dark:border-white/[0.08]">
              <div className="flex aspect-[1.6/1] items-center justify-center bg-black/[0.04] dark:bg-white/[0.04]">
                <img src={item.url} alt={item.alt} className="max-h-full max-w-full object-contain" loading="lazy" />
              </div>
              <div className="flex flex-col gap-2 p-3">
                <div className="truncate text-xs font-medium text-black dark:text-white" title={item.filename}>
                  {item.filename}
                </div>
                <div className="text-[11px] text-gray-500 dark:text-gray-400">
                  {item.width}×{item.height} · {formatBytes(item.size_bytes)}
                </div>
                <input
                  className={`${inputClass} !min-h-[32px] !py-1 text-xs`}
                  value={altDrafts[item.id] ?? item.alt}
                  onChange={(event) => setAltDrafts((prev) => ({ ...prev, [item.id]: event.target.value }))}
                  onBlur={() => saveAlt(item)}
                  placeholder={t("cmsSeo.altPlaceholder", { defaultValue: "Alt text" }) as string}
                />
                <div className="text-[11px] text-gray-500 dark:text-gray-400">
                  {item.used_by.length === 0 ? (
                    t("cmsSeo.mediaUnused", { defaultValue: "Not used yet" })
                  ) : (
                    <span className="flex flex-wrap gap-1">
                      {item.used_by.map((path) => (
                        <PathText key={path}>{path}</PathText>
                      ))}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-xs btn-ghost self-start text-rose-500 normal-case"
                  onClick={() => setDeleteCandidate(item)}
                >
                  {t("common.delete", { defaultValue: "Delete" })}
                </button>
              </div>
            </div>
          ))}
        </div>
        <TablePagination page={page} totalPages={totalPages} onJump={setPage} />
      </AsyncList>

      <ModalConfirmAction
        data={deleteCandidate}
        handler={() => setDeleteCandidate(null)}
        text={
          deleteCandidate
            ? (t("cmsSeo.confirmDeleteMedia", {
                defaultValue: "Delete {{filename}}? Any page still pointing at it will show a broken image.",
                filename: deleteCandidate.filename
              }) as string)
            : ""
        }
        actionText={t("common.delete", { defaultValue: "Delete" })}
        actionClassName="btn-error"
        actionHandler={confirmDelete}
      />
    </div>
  )
}
